import React, { useContext, useEffect } from 'react';
import dashboardContext from '../../../context/dashboard/dashboardContext';
import DashboardLayout from '../shared/DashboardLayout';
import MetricCard from '../shared/MetricCard';
import { formatIndianCurrency } from '../../../utils/currencyFormatter';
import './AdminDashboard.css';

const AdminDashboard = () => {
  const { 
    dashboardData, 
    loading, 
    fetchRestaurantComparison,
    changeRestaurant,
    selectedRestaurant 
  } = useContext(dashboardContext);

  useEffect(() => {
    fetchRestaurantComparison();
  }, []);

  const comparison = dashboardData.restaurantComparison || [];

  const handleRestaurantClick = (restaurantId) => {
    changeRestaurant(restaurantId);
    // Navigate to detailed view
    window.location.href = `/dashboard/owner`;
  };

  // Calculate totals
  const totals = comparison.reduce((acc, restaurant) => ({
    revenue: acc.revenue + restaurant.revenue,
    orders: acc.orders + restaurant.orders,
    avgRating: acc.avgRating + parseFloat(restaurant.customerRating),
    alerts: acc.alerts + restaurant.alerts
  }), { revenue: 0, orders: 0, avgRating: 0, alerts: 0 });

  const avgRating = comparison.length > 0 ? (totals.avgRating / comparison.length).toFixed(1) : 0;
  const avgRevenue = comparison.length > 0 ? Math.round(totals.revenue / comparison.length) : 0;

  return (
    <DashboardLayout 
      title="Admin Dashboard" 
      subtitle="Multi-restaurant overview and comparison"
    >
      {loading && comparison.length === 0 ? (
        <div className="dashboard-loading">
          <div className="loading-spinner"></div>
          <p>Loading restaurant data...</p>
        </div>
      ) : (
        <>
          {/* Network Summary */}
          <div className="admin-summary">
            <h2 className="section-title">🏢 Network Overview</h2>
            <div className="metrics-grid">
              <MetricCard
                title="Total Restaurants"
                value={comparison.length}
                colorScheme="blue"
                icon="🏪"
              />
              
            <MetricCard
              title="Total Revenue (Week)"
              value={totals.revenue}
              unit="₹"
              colorScheme="green"
              subtitle={`Avg per restaurant: ${formatIndianCurrency(avgRevenue, true, 1)}`}
            />
              
              <MetricCard
                title="Total Orders"
                value={totals.orders}
                colorScheme="purple"
              />
              
              <MetricCard
                title="Network Avg Rating"
                value={avgRating}
                unit="/5.0"
                colorScheme="yellow"
              />
              
              <MetricCard
                title="Active Alerts"
                value={totals.alerts}
                colorScheme={totals.alerts > 5 ? 'red' : 'indigo'}
              />
            </div>
          </div>

          {/* Restaurant Comparison */}
          <div className="dashboard-section">
            <h2 className="section-title">📊 Restaurant Comparison</h2>
            
            <div className="comparison-table-container">
              <table className="comparison-table">
                <thead>
                  <tr>
                    <th>Restaurant</th>
                    <th>Location</th>
                    <th>Revenue</th>
                    <th>Change</th>
                    <th>Orders</th>
                    <th>Avg Order Value</th>
                    <th>Profit Margin</th>
                    <th>Rating</th>
                    <th>Efficiency</th>
                    <th>Alerts</th>
                    <th>Action</th>
                  </tr>
                </thead>
                <tbody>
                  {comparison.map((restaurant) => (
                    <tr key={restaurant.id} className="restaurant-row">
                      <td className="restaurant-name">
                        <strong>{restaurant.name}</strong>
                      </td>
                      <td>{restaurant.location}</td>
                      <td className="revenue-cell">
                        {formatIndianCurrency(restaurant.revenue, true, 2)}
                      </td>
                      <td>
                        <span className={`trend-badge ${parseFloat(restaurant.revenueChange) >= 0 ? 'positive' : 'negative'}`}>
                          {parseFloat(restaurant.revenueChange) >= 0 ? '↑' : '↓'} {Math.abs(restaurant.revenueChange)}%
                        </span>
                      </td>
                      <td>{restaurant.orders.toLocaleString('en-IN')}</td>
                      <td>{formatIndianCurrency(restaurant.avgOrderValue, true, 1)}</td>
                      <td>{restaurant.profitMargin}%</td>
                      <td>
                        <span className="rating-badge">
                          ⭐ {restaurant.customerRating}
                        </span>
                      </td>
                      <td>
                        <div className="efficiency-bar">
                          <div 
                            className="efficiency-fill" 
                            style={{ width: `${restaurant.efficiency}%` }}
                          />
                          <span>{restaurant.efficiency}%</span>
                        </div>
                      </td>
                      <td>
                        {restaurant.alerts > 0 ? (
                          <span className="alert-badge">{restaurant.alerts}</span>
                        ) : (
                          <span className="no-alerts">✓</span>
                        )}
                      </td>
                      <td>
                        <button 
                          className="view-btn"
                          onClick={() => handleRestaurantClick(restaurant.id)}
                        >
                          View Details
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>

          {/* Performance Rankings */}
          <div className="dashboard-section">
            <h2 className="section-title">🏆 Performance Rankings</h2>
            
            <div className="rankings-grid">
              <div className="ranking-card">
                <h3>Top Revenue</h3>
                <div className="ranking-list">
                  {[...comparison].sort((a, b) => b.revenue - a.revenue).slice(0, 3).map((restaurant, index) => (
                    <div key={restaurant.id} className="ranking-item">
                      <span className="rank">{index + 1}</span>
                      <span className="rank-name">{restaurant.name}</span>
                      <span className="rank-value">{formatIndianCurrency(restaurant.revenue, true, 2)}</span>
                    </div>
                  ))}
                </div>
              </div>
              
              <div className="ranking-card">
                <h3>Best Customer Rating</h3>
                <div className="ranking-list">
                  {[...comparison].sort((a, b) => parseFloat(b.customerRating) - parseFloat(a.customerRating)).slice(0, 3).map((restaurant, index) => (
                    <div key={restaurant.id} className="ranking-item">
                      <span className="rank">{index + 1}</span>
                      <span className="rank-name">{restaurant.name}</span>
                      <span className="rank-value">⭐ {restaurant.customerRating}</span>
                    </div>
                  ))}
                </div>
              </div>
              
              <div className="ranking-card">
                <h3>Highest Profit Margin</h3>
                <div className="ranking-list">
                  {[...comparison].sort((a, b) => parseFloat(b.profitMargin) - parseFloat(a.profitMargin)).slice(0, 3).map((restaurant, index) => (
                    <div key={restaurant.id} className="ranking-item">
                      <span className="rank">{index + 1}</span>
                      <span className="rank-name">{restaurant.name}</span>
                      <span className="rank-value">{restaurant.profitMargin}%</span>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </>
      )}
    </DashboardLayout>
  );
};

export default AdminDashboard;

